import {
  PencilIcon,
  Trash2Icon,
  User2Icon,
  WorkflowIcon,
  XSquareIcon,
  CopyIcon,
  ArrowLeftIcon
} from "lucide-react";
import { useEffect, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";

import { deleteRole, createRole } from "@/api/role";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MoonLoader } from "react-spinners";

import { useRoles } from "@/hooks/server-state/use-roles";

import { formatCategoryNames } from "@/utils/category";
import { extractCategories } from "@/utils/category";

import AlertModal from "@/components/common/modals/alert-modal";
import RoleModal from "@/components/common/modals/role-modal";
import RoleUserListModal from "@/components/common/modals/role-user-list-model";
import WorkflowListModal from "@/components/common/modals/workflow-list-modal";

import TableSearch, {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow
} from "@/components/ui/table";

import type { CategoryType } from "@/types/category";
import type { RoleType } from "@/types/role";

/* -------------------- state -------------------- */

enum ActionTypes {
  SET_ROLE = "SET_ROLE",
  SET_IS_MODAL_OPEN = "SET_IS_MODAL_OPEN",
  SET_IS_DELETE_MODAL_OPEN = "SET_IS_DELETE_MODAL_OPEN",
  SET_IS_WORKFLOW_LIST_MODAL_OPEN = "SET_IS_WORKFLOW_LIST_MODAL_OPEN",
  SET_IS_ROLE_USERS_LIST_MODAL_OPEN = "SET_IS_ROLE_USERS_LIST_MODAL_OPEN"
}

type State = {
  isDeleteModalOpen: boolean;
  isModalOpen: boolean;
  isWorkflowListModalOpen: boolean;
  isRoleUsersListModalOpen: boolean;
  role: any;
};

type Action = {
  type: ActionTypes;
  payload?: any;
};

const initialState: State = {
  isDeleteModalOpen: false,
  isModalOpen: false,
  isWorkflowListModalOpen: false,
  isRoleUsersListModalOpen: false,
  role: null
};

type CategorySummary = CategoryType & {
  roleCount: number;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const getPageSize = (value: string | null) => {
  const requestedSize = Number(value);

  return PAGE_SIZE_OPTIONS.includes(requestedSize)
    ? requestedSize
    : DEFAULT_PAGE_SIZE;
};

const getCurrentPage = (value: string | null) => {
  const requestedPage = Number(value);

  return Number.isFinite(requestedPage) && requestedPage > 0
    ? requestedPage
    : 1;
};

const matchesRoleSearch = (role: RoleType, searchValue: string) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (!normalizedSearchValue) {
    return true;
  }

  return [
    role.nameFa,
    role.nameEn,
    role.displayNameFa,
    role.displayNameEn,
    role.description
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearchValue));
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.SET_ROLE:
      return { ...state, role: action.payload };

    case ActionTypes.SET_IS_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };

    case ActionTypes.SET_IS_DELETE_MODAL_OPEN:
      return { ...state, isDeleteModalOpen: action.payload };

    case ActionTypes.SET_IS_WORKFLOW_LIST_MODAL_OPEN:
      return { ...state, isWorkflowListModalOpen: action.payload };

    case ActionTypes.SET_IS_ROLE_USERS_LIST_MODAL_OPEN:
      return { ...state, isRoleUsersListModalOpen: action.payload };

    default:
      return state;
  }
}

/* -------------------- component -------------------- */

const RolesPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: rolesResponse, isLoading } = useRoles({ page: 1, size: 50 });

  const [state, dispatch] = useReducer(reducer, initialState);
  const allRoles = rolesResponse?.data ?? [];

  const searchValue = searchParams.get("search") ?? "";
  const currentPage = getCurrentPage(searchParams.get("page"));
  const pageSize = getPageSize(searchParams.get("size"));

  const isAllRolesView = searchParams.get("view") === "all";

  const selectedCategoryIdValue = Number(searchParams.get("categoryId"));
  const selectedCategoryId =
    !isAllRolesView && Number.isFinite(selectedCategoryIdValue) && selectedCategoryIdValue > 0
      ? selectedCategoryIdValue
      : null;

  const categoryRows = useMemo<CategorySummary[]>(() => {
    const categoriesMap = new Map<number, CategorySummary>();

    allRoles.forEach((roleItem: RoleType) => {
      extractCategories(roleItem).forEach((category) => {
        const existingCategory = categoriesMap.get(category.id);

        if (existingCategory) {
          existingCategory.roleCount += 1;
          return;
        }

        categoriesMap.set(category.id, {
          ...category,
          roleCount: 1
        });
      });
    });

    return Array.from(categoriesMap.values()).sort((firstCategory, secondCategory) =>
      firstCategory.name.localeCompare(secondCategory.name, "fa")
    );
  }, [allRoles]);

  const selectedCategory = useMemo(
    () =>
      selectedCategoryId
        ? categoryRows.find((category) => category.id === selectedCategoryId) ?? null
        : null,
    [categoryRows, selectedCategoryId]
  );

  const filteredRoles = useMemo(() => {
    if (isAllRolesView) {
      return allRoles.filter((roleItem: RoleType) => matchesRoleSearch(roleItem, searchValue));
    }

    if (!selectedCategory) {
      return [];
    }

    return allRoles.filter((roleItem: RoleType) => {
      const categoryIds = extractCategories(roleItem).map((category) => category.id);

      return categoryIds.includes(selectedCategory.id) && matchesRoleSearch(roleItem, searchValue);
    });
  }, [allRoles, isAllRolesView, searchValue, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredRoles.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRoles = filteredRoles.slice(startIndex, startIndex + pageSize);

  /* ---------------- DELETE ---------------- */
  const { mutate: deleteItem, isPending: isDeleting } = useMutation({
    mutationFn: (roleId: number) => deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      dispatch({
        type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
        payload: false
      });
    }
  });

  /* ---------------- CLONE ---------------- */
  const { mutate: cloneRole } = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    }
  });

  const handleClone = (roleItem: any) => {
    const clone = {
      id: 0,
      nameFa: roleItem.nameFa + " (copy)",
      nameEn: roleItem.nameEn + "_copy",
      description: roleItem.description,
      displayNameFa: roleItem.displayNameFa,
      displayNameEn: roleItem.displayNameEn
    };

    cloneRole(clone);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.set("categoryId", categoryId.toString());
      params.set("page", "1");
      return params;
    });
  };

  const handleAllRolesSelect = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", "all");
      params.delete("categoryId");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleBackToCategories = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.delete("categoryId");
      params.delete("page");
      params.delete("size");
      return params;
    });
  };

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }

    if (currentPage > totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", totalPages.toString());
        return params;
      });
    }
  }, [currentPage, selectedCategory, setSearchParams, totalPages]);

  if (isLoading) return <Loading />;

  return (
    <>
      {/* ---------------- MODALS ---------------- */}

      <AlertModal
        isOpen={state.isDeleteModalOpen}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          })
        }
        isLoading={isDeleting}
        onConfirm={() => {
          if (!state?.role?.id) return;
          deleteItem(state.role.id);
        }}
        title="آیا از حذف نقش اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود"
      />

      <RoleModal
        isOpen={state.isModalOpen}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_MODAL_OPEN,
            payload: false
          })
        }
        role={state.role}
      />

      <WorkflowListModal
        isOpen={state.isWorkflowListModalOpen}
        roleId={state?.role?.id}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_WORKFLOW_LIST_MODAL_OPEN,
            payload: false
          })
        }
      />

      <RoleUserListModal
        isOpen={state.isRoleUsersListModalOpen}
        roleId={state?.role?.id}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_ROLE_USERS_LIST_MODAL_OPEN,
            payload: false
          })
        }
      />

      {selectedCategory || isAllRolesView ? (
        <>
          {/* ---------------- HEADER ---------------- */}

          <div className="flex items-start justify-between gap-3 px-5 py-2">
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={handleBackToCategories}
              >
                <ArrowLeftIcon size={14} className="text-primary" />
                بازگشت به دسته بندی ها
              </button>

              <div className="text-sm text-slate-600">
                {isAllRolesView ? (
                  <>
                    همه نقش ها
                  </>
                ) : (
                  <>
                    نقش‌های دسته بندی <span className="font-semibold text-slate-900">{selectedCategory?.name}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <TableSearch className="h-8 w-72 max-w-full" placeholder="جستجوی نقش ها" />
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={() => {
                  dispatch({ type: ActionTypes.SET_ROLE, payload: null });
                  dispatch({ type: ActionTypes.SET_IS_MODAL_OPEN, payload: true });
                }}
              >
                <XSquareIcon size={14} className="text-primary" />
                ساخت نقش جدید
              </button>
            </div>
          </div>

          {/* ---------------- TABLE ---------------- */}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام</TableHead>
                <TableHead>نام انگلیسی</TableHead>
                <TableHead>توضیحات</TableHead>
                <TableHead>دسته بندی ها</TableHead>
                <TableHead>گردش کار ها</TableHead>
                <TableHead>کاربر ها</TableHead>
                <TableHead>ویرایش</TableHead>
                <TableHead>کلون</TableHead>
                <TableHead>حذف</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {visibleRoles.map((roleItem: any, index: number) => (
                <TableRow key={roleItem.id}>
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{roleItem.nameFa}</TableCell>
                  <TableCell>{roleItem.nameEn}</TableCell>
                  <TableCell>{roleItem.description}</TableCell>

                  <TableCell className="max-w-xs whitespace-normal">
                    {formatCategoryNames(roleItem) || "-"}
                  </TableCell>

                  {/* workflows */}
                  <TableCell>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ActionTypes.SET_ROLE,
                          payload: roleItem
                        });
                        dispatch({
                          type: ActionTypes.SET_IS_WORKFLOW_LIST_MODAL_OPEN,
                          payload: true
                        });
                      }}
                    >
                      <WorkflowIcon className="text-slate-700" />
                    </button>
                  </TableCell>

                  {/* users */}
                  <TableCell>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ActionTypes.SET_ROLE,
                          payload: roleItem
                        });
                        dispatch({
                          type: ActionTypes.SET_IS_ROLE_USERS_LIST_MODAL_OPEN,
                          payload: true
                        });
                      }}
                    >
                      <User2Icon className="text-slate-700" />
                    </button>
                  </TableCell>

                  {/* edit */}
                  <TableCell>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ActionTypes.SET_ROLE,
                          payload: roleItem
                        });
                        dispatch({
                          type: ActionTypes.SET_IS_MODAL_OPEN,
                          payload: true
                        });
                      }}
                    >
                      <PencilIcon className="text-slate-700" />
                    </button>
                  </TableCell>

                  {/* clone */}
                  <TableCell>
                    <button onClick={() => handleClone(roleItem)}>
                      <CopyIcon className="text-slate-700" />
                    </button>
                  </TableCell>

                  {/* delete */}
                  <TableCell>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ActionTypes.SET_ROLE,
                          payload: roleItem
                        });

                        dispatch({
                          type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
                          payload: true
                        });
                      }}
                    >
                      <Trash2Icon className="text-red-500" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* ---------------- EMPTY ---------------- */}

          {!visibleRoles.length && (
            <EmptyState message="نقشی برای این دسته بندی پیدا نشد" />
          )}

          <TablePagination totalItems={filteredRoles.length} />
        </>
      ) : (
        <>
          {/* ---------------- HEADER ---------------- */}

          <div className="flex items-start justify-between gap-3 px-5 py-2">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-slate-900">دسته بندی نقش ها</p>
              <p className="mt-1 text-xs text-slate-500">یک دسته بندی را انتخاب کنید تا نقش های مربوط به آن نمایش داده شود.</p>
            </div>

            <button
              className="flex items-center gap-x-1 text-sm hover:text-primary"
              onClick={() => {
                dispatch({ type: ActionTypes.SET_ROLE, payload: null });
                dispatch({ type: ActionTypes.SET_IS_MODAL_OPEN, payload: true });
              }}
            >
              <XSquareIcon size={14} className="text-primary" />
              ساخت نقش جدید
            </button>
          </div>

          {/* ---------------- TABLE ---------------- */}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام دسته بندی</TableHead>
                <TableHead>تعداد نقش</TableHead>
                <TableHead>توضیحات</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow
                className="cursor-pointer"
                onClick={handleAllRolesSelect}
              >
                <TableCell>{1}</TableCell>
                <TableCell className="font-medium text-primary">همه</TableCell>
                <TableCell>{allRoles.length}</TableCell>
                <TableCell>نمایش همه نقش ها</TableCell>
              </TableRow>

              {categoryRows.map((categoryItem, index) => (
                <TableRow
                  key={categoryItem.id}
                  className="cursor-pointer"
                  onClick={() => handleCategorySelect(categoryItem.id)}
                >
                  <TableCell>{index + 2}</TableCell>
                  <TableCell>{categoryItem.name}</TableCell>
                  <TableCell>{categoryItem.roleCount}</TableCell>
                  <TableCell>{categoryItem.description || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* ---------------- EMPTY ---------------- */}

          {!categoryRows.length && <EmptyState message="دسته بندی پیدا نشد" />}
        </>
      )}
    </>
  );
};

/* ---------------- UI ---------------- */

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({ message = "نقشی پیدا نشد" }: { message?: string }) => (
  <div className="flex h-32 items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default RolesPage;
